'use client';

import React, { useState } from 'react';
import Icon from '@/components/ui/AppIcon';
import { motion, AnimatePresence } from 'framer-motion';
import RisingParticlesScene from './RisingParticlesScene';
import MobileServicesConstellation from './MobileServicesConstellation';

interface Service {
    id: number;
    icon: string;
    title: string;
    description: string;
    features: string[];
    color: string;
    bg: string;
    // We will calculate position dynamically based on index for a perfect circle/grid
}

const ServicesGrid = () => {
    const [hoveredId, setHoveredId] = useState<number | null>(null);

    const services: Service[] = [
        {
            id: 1,
            icon: 'MapPinIcon',
            title: 'Google Business Profile',
            description: 'Maximize your local visibility with expert GBP management and optimization strategies.',
            features: ['Profile setup', 'Keyword optimization', 'Review management'],
            color: 'text-blue-600',
            bg: 'bg-blue-100'
        },
        {
            id: 2,
            icon: 'MagnifyingGlassIcon',
            title: 'Strategic SEO',
            description: 'Drive organic traffic with data-driven SEO strategies tailored to your business goals.',
            features: ['SEO audit', 'On-page optimization', 'Technical SEO'],
            color: 'text-purple-600',
            bg: 'bg-purple-100'
        },
        {
            id: 3,
            icon: 'StarIcon',
            title: 'Reputation',
            description: 'Build trust and credibility with proactive online reputation monitoring and management.',
            features: ['Review monitoring', 'Response crafting', 'Sentiment analysis'],
            color: 'text-amber-600',
            bg: 'bg-amber-100'
        },
        {
            id: 4,
            icon: 'ChartBarIcon',
            title: 'Local SEO',
            description: 'Capture local customers with targeted geographic optimization and citation building.',
            features: ['Citation building', 'NAP consistency', 'Local link building'],
            color: 'text-emerald-600',
            bg: 'bg-emerald-100'
        },
        {
            id: 5,
            icon: 'DocumentTextIcon',
            title: 'Content Strategy',
            description: 'Engage your audience with SEO-optimized content that drives conversions.',
            features: ['Content calendar', 'Blog posts', 'Performance tracking'],
            color: 'text-rose-600',
            bg: 'bg-rose-100'
        },
        {
            id: 6,
            icon: 'PresentationChartLineIcon',
            title: 'Analytics',
            description: 'Make informed decisions with comprehensive analytics and actionable insights.',
            features: ['Custom dashboards', 'ROI measurement', 'Strategy sessions'],
            color: 'text-indigo-600',
            bg: 'bg-indigo-100'
        }
    ];

    const centerNode = {
        id: 7,
        icon: 'RocketLaunchIcon',
        title: 'Exponential Growth',
        description: 'The accumulation of all strategies leading to explosive brand authority and ROI.',
        features: ['Market Dominance', 'Revenue Scaling', 'Brand Authority'],
        color: 'text-white',
        bg: 'bg-slate-900' // Dark central node for "Nexus" feel
    };

    // Calculate Satellite Positions (Perfect Hexagon/Circle)
    const radius = 35; // % from center
    const center = { x: 50, y: 50 };

    // Explicit ordering for visual balance: top-left, top, top-right, etc. matches the array order mapped to angle
    // -90 is top. We want 6 items. 360/6 = 60deg.
    // Angles: 210, 270, 330, 30, 90, 150 (starting from left-ish to go clockwise appropriately)
    // Actually, let's just distribute them evenly starting from -90 (Top) ?? 
    // Let's do: Top-Left, Top, Top-Right, Bottom-Right, Bottom, Bottom-Left... 
    // Array indices: 0, 1, 2, 3, 4, 5
    // Let's use angles: 220, 270, 320, 40, 90, 140 ... roughly.
    // Simpler: 6 items distributed evenly is 60 deg apart.
    // We want 1 at top (270 or -90), or maybe 2 at top?
    // Let's do 2 columns of 3? Or a circle? The prompt mentioned "Structured/Grid-like".
    // A circle with pill cards is very clean. Let's do that.

    // Position helper
    const getPos = (index: number, total: number) => {
        // Start angle: -60 (Top Rightish) -> 0 -> 60 -> 120 -> 180 -> 240
        // Adjust to have 0 at right.
        const angleOffset = -60;
        const angleDeg = (360 / total) * index + angleOffset;
        const angleRad = (angleDeg * Math.PI) / 180;

        // Elliptical adjustment if needed (wider x)
        const x = center.x + (radius * 1.3) * Math.cos(angleRad); // Wider spread horizontally
        const y = center.y + radius * Math.sin(angleRad);

        // Return with fixed precision to match server/client
        return {
            x: Number(x.toFixed(4)),
            y: Number(y.toFixed(4)),
            angle: angleDeg
        };
    };

    return (
        <section className="py-32 bg-slate-50 relative overflow-visible min-h-[900px] flex flex-col items-center border-t border-slate-200 z-10">
            {/* Structured Grid Background */}
            <div className="absolute inset-0 opacity-[0.05]"
                style={{
                    backgroundImage: 'linear-gradient(90deg, #64748b 1px, transparent 1px), linear-gradient(#64748b 1px, transparent 1px)',
                    backgroundSize: '80px 80px'
                }}
            />
            {/* Gradient Overlay for Vignette */}
            <div className="absolute inset-0 bg-radial-gradient from-transparent via-transparent to-slate-50/80 pointer-events-none" />

            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 w-full relative z-10 flex-grow flex flex-col">
                <div className="text-center max-w-3xl mx-auto mb-20">
                    <motion.div
                        initial={{ opacity: 0, y: 20 }}
                        whileInView={{ opacity: 1, y: 0 }}
                        viewport={{ once: true }}
                    >
                        <span className="inline-block px-4 py-1.5 rounded-full bg-white border border-slate-200 text-slate-500 text-xs font-bold uppercase tracking-wider mb-6 shadow-sm">
                            Integrated Ecosystem
                        </span>
                        <h2 className="text-4xl lg:text-5xl font-black text-slate-900 mb-6 tracking-tight">
                            The Strategic <span className="text-transparent bg-clip-text bg-gradient-to-r from-indigo-600 to-violet-600">Nexus</span>
                        </h2>
                        <p className="text-lg text-slate-600 leading-relaxed">
                            A precision-engineered constellation of services designed to work in perfect synchronization.
                        </p>
                    </motion.div>
                </div>

                {/* Mobile View (Preserved) */}
                <MobileServicesConstellation />

                {/* Desktop "Nexus" View */}
                <div className="hidden lg:block relative w-full h-[650px] mx-auto">

                    {/* SVG Connections Layer */}
                    <svg className="absolute inset-0 w-full h-full pointer-events-none z-0">
                        {services.map((service, i) => {
                            const pos = getPos(i, services.length);
                            return (
                                <motion.g key={`link-${i}`}>
                                    {/* Connection Line */}
                                    <line
                                        x1={`${center.x}%`} y1={`${center.y}%`}
                                        x2={`${pos.x}%`} y2={`${pos.y}%`}
                                        stroke="#e2e8f0"
                                        strokeWidth="2"
                                    />
                                    {/* Animated Pulse on Line */}
                                    {/* <motion.circle r="3" fill="#64748b">
                                        <animateMotion 
                                            dur={`${3 + i}s`} 
                                            repeatCount="indefinite"
                                            path={`M ${center.x*10} ${center.y*8} L ${pos.x*10} ${pos.y*8}`} // Note: path mapping in React SVG is tricky with % vs userUnits. 
                                            // Simplifying: Just use CSS styling or simpler animations.
                                        />
                                    </motion.circle> */}
                                </motion.g>
                            );
                        })}
                    </svg>

                    {/* Central Hub Node */}
                    <motion.div
                        className="absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 z-20 cursor-pointer"
                        initial={{ scale: 0, opacity: 0 }}
                        whileInView={{ scale: 1, opacity: 1 }}
                        transition={{ type: "spring", duration: 1, bounce: 0.5 }}
                        onHoverStart={() => setHoveredId(centerNode.id)}
                        onHoverEnd={() => setHoveredId(null)}
                    >
                        {/* Pulse Ring */}
                        <div className="absolute inset-0 rounded-full bg-indigo-500/10 animate-[ping_3s_linear_infinite]" />
                        <div className="absolute -inset-4 rounded-full border border-indigo-100 opacity-50" />

                        <div className="w-32 h-32 rounded-full bg-slate-900 shadow-2xl shadow-indigo-500/30 flex flex-col items-center justify-center border-4 border-white relative z-10 overflow-hidden group">
                            {/* Shine Effect */}
                            <div className="absolute inset-0 bg-gradient-to-tr from-white/10 to-transparent opacity-0 group-hover:opacity-100 transition-opacity" />

                            <Icon name={centerNode.icon} size={40} className="text-white mb-2" variant="solid" />
                            <span className="text-[10px] font-bold text-white/80 uppercase tracking-widest">Growth</span>
                        </div>
                    </motion.div>


                    {/* Satellite Nodes (Pill Cards) */}
                    {services.map((service, i) => {
                        const pos = getPos(i, services.length);
                        const isHovered = hoveredId === service.id;
                        const isCenterHovered = hoveredId === centerNode.id;

                        return (
                            <motion.div
                                key={service.id}
                                className="absolute"
                                style={{
                                    left: `${pos.x}%`,
                                    top: `${pos.y}%`
                                }}
                                initial={{ opacity: 0, scale: 0.8 }}
                                whileInView={{ opacity: 1, scale: 1 }}
                                transition={{ delay: i * 0.1 }}
                            >
                                <motion.div
                                    className={`
                                        relative -translate-x-1/2 -translate-y-1/2 
                                        flex items-center gap-4 bg-white rounded-full p-2 pr-6 
                                        shadow-lg border border-slate-100 cursor-pointer
                                        transition-all duration-300
                                        ${isHovered ? 'scale-110 shadow-xl border-indigo-200 z-30' : 'hover:border-slate-300 z-10'}
                                        ${isCenterHovered ? 'opacity-50 blur-[1px]' : 'opacity-100'}
                                    `}
                                    onHoverStart={() => setHoveredId(service.id)}
                                    onHoverEnd={() => setHoveredId(null)}
                                    layout
                                >
                                    {/* Icon Circle */}
                                    <div className={`w-12 h-12 rounded-full ${service.bg} flex items-center justify-center shrink-0`}>
                                        <Icon name={service.icon} size={20} className={service.color} variant="solid" />
                                    </div>

                                    {/* Text Content */}
                                    <div className="flex flex-col">
                                        <span className="font-bold text-slate-800 text-sm">{service.title}</span>
                                        {/* Show simple subtitle usually, but features on hover? */}
                                        <span className="text-[10px] font-bold text-slate-400 uppercase tracking-wider">
                                            VIEW DETAILS
                                        </span>
                                    </div>

                                    {/* Tooltip Card (Appears on Hover) */}
                                    <AnimatePresence>
                                        {isHovered && (
                                            <motion.div
                                                initial={{ opacity: 0, y: 10, scale: 0.95 }}
                                                animate={{ opacity: 1, y: 0, scale: 1 }}
                                                exit={{ opacity: 0, y: 10, scale: 0.95 }}
                                                className="absolute top-full left-1/2 -translate-x-1/2 mt-4 w-72 bg-white rounded-2xl p-6 shadow-2xl border border-slate-100 z-50 text-left"
                                            >
                                                {/* Arrow */}
                                                <div className="absolute -top-2 left-1/2 -translate-x-1/2 w-4 h-4 bg-white rotate-45 border-t border-l border-slate-100" />

                                                <h4 className={`text-lg font-bold mb-2 ${service.color}`}>{service.title}</h4>
                                                <p className="text-slate-600 text-xs mb-4 leading-relaxed">{service.description}</p>

                                                <div className="space-y-2">
                                                    {service.features.map((f, idx) => (
                                                        <div key={idx} className="flex items-center text-xs font-semibold text-slate-500 bg-slate-50 px-2 py-1.5 rounded-lg">
                                                            <div className={`w-1.5 h-1.5 rounded-full mr-2 ${service.bg.replace('bg-', 'bg-')}-500`} />
                                                            {/* Note: Tailwind dynamic class reconstruction often needs help, but assuming bg-blue-100 -> blue-500 logic works if classes exist */}
                                                            {f}
                                                        </div>
                                                    ))}
                                                </div>
                                            </motion.div>
                                        )}
                                    </AnimatePresence>

                                </motion.div>
                            </motion.div>
                        );
                    })}

                    {/* Orbit Ring (Decoration) */}
                    <svg className="absolute inset-0 w-full h-full pointer-events-none z-0 opacity-20">
                        <ellipse cx="50%" cy="50%" rx="35%" ry="27%" fill="none" stroke="#64748b" strokeWidth="1" strokeDasharray="4 4" />
                    </svg>

                </div>
            </div>
        </section>
    );
};

export default ServicesGrid;